// Phase 14e-1c — operator-facing shift-request admin surface.
//
// Per-resource admin-side schema, sibling to the customer-facing
// shift-request.schema.ts. Drives the `/admin/shift-requests/$id`
// detail page that hosts the manual-target caregiver picker (14e-a-7).
//
// Phase 14e-c-10 — adds the shift LIST admin surface (the 14e-a-7
// list endpoint scaling watch item activated). Drives the new shift
// sections on Caregiver / Facility / Homecare detail pages. Cancel-
// by-operator + record-manual-payment (homecare only) buttons surface
// per row.
import { z } from 'zod';
import { MatchingStateSchema, ShiftStatusSchema } from './enums.schema.js';
/** GET /api/v1/admin/shift-requests/:id response.
 *
 *  Joined denormalised view of the ShiftRequest row + operator-needed
 *  context (parent-entity name, requesting user identity, role code,
 *  attempt count, currently-targeted caregiver if any). The page
 *  renders the request detail + a "Target a caregiver" button that
 *  opens the picker modal. */
export const AdminShiftRequestDetailResponseSchema = z.object({
    id: z.string().uuid(),
    // Requesting user
    requestedByUserId: z.string().uuid(),
    requestedByUserEmail: z.string().email(),
    requestedByUserName: z.string(),
    // Polymorphic parent entity — exactly one of (facility / homecare
    // client + recipient) is non-null per the ShiftRequest CHECK
    facilityId: z.string().uuid().nullable(),
    facilityName: z.string().nullable(),
    homecareClientId: z.string().uuid().nullable(),
    homecareClientName: z.string().nullable(),
    recipientId: z.string().uuid().nullable(),
    recipientLabel: z.string().nullable(),
    // Shift spec
    roleId: z.string().uuid(),
    roleCode: z.string(),
    setting: z.enum(['LTC', 'HOMECARE']),
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    /** Geocoded shift location — used by the manual-target picker for
     *  client-side haversine distance display per 14e-a-8. Null when
     *  geocoding hasn't run yet (rare; new requests usually have it). */
    locationLat: z.string().nullable(),
    locationLng: z.string().nullable(),
    specialConstraints: z.string().nullable(),
    requiredSkills: z.array(z.string()),
    // Matching state
    matchingState: MatchingStateSchema,
    offerTierSize: z.number().int().positive(),
    /** Total ShiftOffers created against this request — equals the auto-
     *  matcher's attempt count + any operator manual targets. */
    attemptCount: z.number().int().nonnegative(),
    lastAttemptAt: z.string().datetime().nullable(),
    /** Most recent PENDING / ACCEPTED offer's caregiver, if any. Null when
     *  no offers exist yet OR all offers are DECLINED / EXPIRED. */
    currentTargetCaregiverId: z.string().uuid().nullable(),
    currentTargetCaregiverName: z.string().nullable(),
    // Lifecycle
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    cancelledAt: z.string().datetime().nullable(),
    cancelReason: z.string().nullable(),
});
// ─── Phase 14e-c-10 — shift list ─────────────────────────────────────────────
/** GET /api/v1/admin/shifts query — EXACTLY ONE of facilityId /
 *  caregiverId / homecareClientId must be provided. The endpoint
 *  returns shifts associated with that entity, regardless of who owns
 *  the originating ShiftRequest. */
export const AdminShiftListQuerySchema = z.object({
    facilityId: z.string().uuid().optional(),
    caregiverId: z.string().uuid().optional(),
    homecareClientId: z.string().uuid().optional(),
    status: ShiftStatusSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
/** Denormalised list row — column sources vary by persona perspective
 *  but the row shape is uniform so a single <ShiftsCard> component
 *  serves all 3 detail pages. */
export const AdminShiftListItemSchema = z.object({
    id: z.string().uuid(),
    status: ShiftStatusSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    roleCode: z.string(),
    setting: z.enum(['LTC', 'HOMECARE']),
    /** Polymorphic counterparties — at least one will be populated. */
    caregiverId: z.string().uuid(),
    caregiverName: z.string(),
    facilityId: z.string().uuid().nullable(),
    facilityName: z.string().nullable(),
    homecareClientId: z.string().uuid().nullable(),
    homecareClientName: z.string().nullable(),
    recipientId: z.string().uuid().nullable(),
    recipientLabel: z.string().nullable(),
    payRateAtBooking: z.string(),
    billRateAtBooking: z.string(),
    /**
     * Homecare Stripe pre-auth lifecycle state. Drives the admin
     * record-manual-payment button gate: the escalation button only makes
     * sense while payment is still unsettled — CAPTURED / MANUAL_PAID mean
     * the shift is already paid, so surfacing the button there invites a
     * double-charge attempt (backend now rejects it with `invoice_already_paid`,
     * but the button shouldn't appear at all).
     */
    preAuthStatus: z.enum([
        'NOT_REQUIRED',
        'PENDING',
        'AUTHORIZED',
        'FAILED',
        'CAPTURED',
        'RELEASED',
        'MANUAL_PAID',
    ]),
    cancelledAt: z.string().datetime().nullable(),
    cancelReason: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminShiftListResponseSchema = z.object({
    items: z.array(AdminShiftListItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ─── Phase 21 (3.1-6) — request-centric unified operator list ─────────────────
/** The derived merged status — collapses a posting's two lifecycle halves
 *  (ShiftRequest.matchingState + the live Shift.status, if any) into a single
 *  left-to-right lifecycle the operator reads at a glance. Computed at read
 *  time; never stored. */
export const MergedShiftStatusSchema = z.enum([
    'QUEUED', // future-dated, not yet within the broadcast horizon
    'MATCHING', // broadcasting / targeted, no live booking yet
    'FILLED_SCHEDULED', // booked, not ended yet
    'NEEDS_SIGNOFF', // booked, ended, timesheet not signed (doc 17 A5 — same rule as the portal;
    //                  admin labels it "Awaiting sign-off", the facility sees "Needs sign-off")
    'IN_PROGRESS', // booked, underway
    'COMPLETED', // done
    'NO_SHOW', // booked but no-show
    'DISPUTED', // booked, under dispute
    'CANCELLED', // request cancelled, no live booking
    'WITHDRAWN', // buyer withdrew it while unfilled — excluded from fill rate
    'UNFILLED_EXPIRED', // matching gave up, never filled
]);
/** Coarse status buckets for the operator's default filter. Each maps to a set
 *  of merged statuses (see the service's group→where translation). */
export const ShiftRequestStatusGroupSchema = z.enum([
    'needs_attention', // MATCHING · UNFILLED_EXPIRED · DISPUTED · NO_SHOW · NEEDS_SIGNOFF
    'open', // still actively seeking: MATCHING · REBROADCASTING_T2 · PENDING_TARGETED
    'upcoming', // QUEUED · FILLED_SCHEDULED (not ended — doc 17 A1)
    'active', // IN_PROGRESS
    'done', // COMPLETED · CANCELLED · WITHDRAWN
]);
/** GET /api/v1/admin/shift-requests query. All filters optional (omit → all). */
export const AdminShiftRequestListQuerySchema = z.object({
    statusGroup: ShiftRequestStatusGroupSchema.optional(),
    setting: z.enum(['LTC', 'HOMECARE']).optional(),
    /** Inclusive scheduled-start window (ISO date or datetime). */
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
    /** Case-insensitive substring over the buyer name (facility or homecare client). */
    search: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
/** One row per posting. `assigned*` + `liveShiftId` are populated only when a
 *  live (non-cancelled) booking exists — that shift is what void-and-reassign
 *  acts on. */
export const AdminShiftRequestListItemSchema = z.object({
    requestId: z.string().uuid(),
    mergedStatus: MergedShiftStatusSchema,
    /** Raw matching state, for operators who want the underlying detail. */
    matchingState: MatchingStateSchema,
    roleCode: z.string(),
    setting: z.enum(['LTC', 'HOMECARE']),
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    /** Buyer — exactly one of facility / homecare is populated. */
    facilityId: z.string().uuid().nullable(),
    facilityName: z.string().nullable(),
    homecareClientId: z.string().uuid().nullable(),
    homecareClientName: z.string().nullable(),
    recipientLabel: z.string().nullable(),
    /** Live booking, if any. */
    liveShiftId: z.string().uuid().nullable(),
    assignedCaregiverId: z.string().uuid().nullable(),
    assignedCaregiverName: z.string().nullable(),
    preferredCaregiverId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminShiftRequestListResponseSchema = z.object({
    items: z.array(AdminShiftRequestListItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/** Response for POST /admin/shift-requests/:id/cancel (2026-08-11 fix).
 *  `cancelledShiftId` = the booked shift cancelled, or null when the posting
 *  was pre-shift and the request itself was cancelled. */
export const AdminShiftRequestCancelResponseSchema = z.object({
    cancelled: z.literal(true),
    cancelledShiftId: z.string().uuid().nullable(),
});
// The candidate-caregivers (eligible-only picker) schema lives in its own
// resource file — see ./candidate-caregivers.schema.ts (Convention 06).
//# sourceMappingURL=admin.schema.js.map